import { useEffect, useState } from 'react'

import classNames from 'classnames'
import { toast } from 'react-toastify'
import { useSignMessage, useSwitchChain } from 'wagmi'

import { apiCreateFeatherOrderWithDepositDetails, apiSignFeatherQuote } from '@src/api/feather.api'
import ChevronLeftIcon from '@src/components/icons/ChevronLeft'
import ClockIcon from '@src/components/icons/Clock'
import Copy from '@src/components/icons/Copy'
import WarningIcon from '@src/components/icons/WarningIcon'
import { ResultCode, StepNames } from '@src/enums'
import { useDepositTransaction } from '@src/hooks/evm/useDepositTransaction'
import { useWalletConnections } from '@src/hooks/useWalletConnections'
import { IExchangeData } from '@src/types/exchange'
import { IFee } from '@src/types/fee'
import { EVMAddress } from '@src/types/token'
import { Balance } from '@src/utils/balance'
import { createContextLogger } from '@src/utils/logger'
import { useConfigStore } from '@src/zustand_stores'
import { useApplicationStore } from '@src/zustand_stores/application.store'
import { useHistoryStore } from '@src/zustand_stores/history.store'

const logger = createContextLogger('FeatherOrderSummary')

interface IFeatherOrderSummaryProps {
    onClose: () => void
    onConfirm: () => Promise<void>
    sellData: IExchangeData
    buyData: IExchangeData
    fees: IFee[]
    depositAddress?: string
}

export default function FeatherOrderSummary({
    onClose,
    onConfirm,
    sellData,
    buyData,
    fees,
    depositAddress,
}: IFeatherOrderSummaryProps) {
    const { exchangeId, setWaitingForConnectedWalletInteraction } = useApplicationStore()
    const updateHistory = useHistoryStore(state => state.updateHistory)
    const availableNetworks = useConfigStore(state => state.availableNetworks)

    const { evmWalletAddress, EVMChainId } = useWalletConnections()
    const { switchChainAsync } = useSwitchChain()
    const { signMessageAsync } = useSignMessage()
    const { sendDepositTransaction } = useDepositTransaction()

    const [showQR, setShowQR] = useState(false)
    const [signatureStatus, setSignatureStatus] = useState<'unsigned' | 'signing' | 'signed' | 'error'>('unsigned')
    const [remainingTimeMs, setRemainingTimeMs] = useState<number>(0)
    const [isTransacting, setIsTransacting] = useState(false)
    const [isSwitchingNetwork, setIsSwitchingNetwork] = useState(false)

    // Set up initial timer value and start countdown interval
    useEffect(() => {
        // Initial calculation
        const expiryTime = sellData.featherExpiresAt ? sellData.featherExpiresAt * 1000 : 0
        const initialRemainingTime = Math.max(0, expiryTime - Date.now())
        setRemainingTimeMs(initialRemainingTime)

        // Set up interval to update every second
        const timerInterval = setInterval(() => {
            setRemainingTimeMs(prev => {
                const newTime = Math.max(0, prev - 1000)
                // If timer reaches zero, clear the interval
                if (newTime <= 0 && timerInterval) {
                    clearInterval(timerInterval)
                }
                return newTime
            })
        }, 1000)

        // Clean up interval on component unmount or when quote changes
        return () => clearInterval(timerInterval)
    }, [sellData.featherExpiresAt])

    // Derived timer values from remainingTimeMs
    const isExpired = remainingTimeMs <= 0
    const formattedHours = Math.max(0, Math.floor(remainingTimeMs / (1000 * 60 * 60)))
    const formattedMinutes = Math.max(0, Math.floor((remainingTimeMs % (1000 * 60 * 60)) / (1000 * 60)))
    const formattedSeconds = Math.max(0, Math.floor((remainingTimeMs % (1000 * 60)) / 1000))

    // Determine if we should show hours:minutes or minutes:seconds
    const showHoursFormat = formattedHours > 0

    // Format the timer display based on remaining time
    const timerDisplay = showHoursFormat
        ? `${formattedHours}h ${formattedMinutes}m`
        : `${formattedMinutes}m ${formattedSeconds}s`

    // Get network info - for Feather flows
    const sourceNetworkName = availableNetworks[sellData.chainId]?.name || 'Unknown Network'
    const sourceNetworkIcon = availableNetworks[sellData.chainId]?.icon || sellData.token.logoURI

    // Toggle QR code display
    const toggleQR = () => {
        setShowQR(!showQR)
    }

    // Handle copy to clipboard
    const handleCopyDepositAddress = () => {
        if (depositAddress) {
            navigator.clipboard.writeText(depositAddress)
            toast.success('Address copied to clipboard', {
                position: 'bottom-right',
                autoClose: 2000,
            })
        }
    }

    // Function to handle automatic transaction creation and sending
    const handleSendTransaction = async () => {
        if (!depositAddress || !sellData.value || !sellData.token || !sellData.featherQuoteId) {
            toast.error('Missing required data for transaction', {
                position: 'bottom-right',
                autoClose: 5000,
            })
            return
        }

        if (!evmWalletAddress) {
            toast.error('Wallet not connected', {
                position: 'bottom-right',
                autoClose: 5000,
            })
            return
        }

        try {
            setIsTransacting(true)
            setWaitingForConnectedWalletInteraction(true)

            // Check if we need to switch networks
            if (EVMChainId !== sellData.chainId) {
                logger.info('Switching network', {
                    from: EVMChainId,
                    to: sellData.chainId,
                })

                setIsSwitchingNetwork(true)
                toast.info(`Switching to ${availableNetworks[sellData.chainId]?.name} network...`, {
                    position: 'bottom-right',
                    autoClose: 5000,
                })

                try {
                    await switchChainAsync({ chainId: sellData.chainId })
                    toast.success(`Successfully switched to ${availableNetworks[sellData.chainId]?.name}`, {
                        position: 'bottom-right',
                        autoClose: 3000,
                    })
                } catch (switchError: any) {
                    logger.error('Error switching networks:', switchError)

                    // Check if user rejected the network switch
                    if (switchError.code === 4902) {
                        toast.error('Network not added to wallet. Please add it manually.', {
                            position: 'bottom-right',
                            autoClose: 5000,
                        })
                    } else if (switchError.code === 4001) {
                        toast.error('Network switch rejected. Please try again.', {
                            position: 'bottom-right',
                            autoClose: 5000,
                        })
                    } else {
                        toast.error(`Failed to switch networks: ${switchError.message || 'Unknown error'}`, {
                            position: 'bottom-right',
                            autoClose: 5000,
                        })
                    }

                    throw new Error('Network switch failed. Please switch networks manually and try again.')
                } finally {
                    setIsSwitchingNetwork(false)
                }
            }

            logger.info('Creating automatic deposit transaction', {
                depositAddress,
                tokenAddress: sellData.token.address,
                amount: sellData.value,
                chainId: sellData.chainId,
            })

            // Use the modern hook to create and send the transaction
            const txHash = await sendDepositTransaction(
                depositAddress as EVMAddress,
                sellData.token.address,
                BigInt(sellData.value.toString()),
                sellData.chainId
            )

            console.log('🚀 Transaction sent, hash:', txHash)

            // SIMPLE APPROACH: Just set the step and call onConfirm immediately
            updateHistory(exchangeId, {
                currentStep: StepNames.FEATHER_WAITING_FOR_DEPOSIT,
                result: {
                    status: ResultCode.pending,
                    message: 'Processing your swap...',
                    sourceTxHash: txHash,
                },
            })

            // Call onConfirm synchronously to force the UI update
            try {
                onConfirm()
            } catch (error) {
                console.error('Error in onConfirm, but continuing anyway:', error)
            }

            // Now try to create the order in the background
            apiCreateFeatherOrderWithDepositDetails(sellData.featherQuoteId, evmWalletAddress, txHash)
                .then(orderResponse => {
                    console.log('🚀 Order created successfully:', orderResponse)

                    // Update history with order ID
                    updateHistory(exchangeId, {
                        orderId: orderResponse.orderId,
                    })

                    toast.success('Order created successfully', {
                        position: 'bottom-right',
                        autoClose: 3000,
                    })
                })
                .catch(error => {
                    console.error('🚀 Failed to create order, but transaction was sent:', error)

                    toast.warning('Transaction sent, but order creation failed. Support has been notified.', {
                        position: 'bottom-right',
                        autoClose: 5000,
                    })
                })
        } catch (error: any) {
            logger.error('Error sending transaction:', error)

            // Check if user rejected the transaction
            if (error.code === 4001) {
                toast.error('Transaction was rejected. Please try again or deposit manually.', {
                    position: 'bottom-right',
                    autoClose: 5000,
                })
            } else {
                toast.error(`Failed to send transaction: ${error.message || 'Unknown error'}`, {
                    position: 'bottom-right',
                    autoClose: 5000,
                })
            }
        } finally {
            setIsTransacting(false)
            setWaitingForConnectedWalletInteraction(false)
        }
    }

    // Handle signing the quote
    const handleSignQuote = async () => {
        if (!sellData.featherQuoteId || !evmWalletAddress) {
            toast.error('Cannot sign quote: Missing quote ID or wallet connection', {
                position: 'bottom-right',
                autoClose: 5000,
            })
            setSignatureStatus('error')
            return
        }

        try {
            setSignatureStatus('signing')
            setWaitingForConnectedWalletInteraction(true)

            const message = `Feather quote ${sellData.featherQuoteId}`

            console.log('Requesting signature for', { message, evmWalletAddress })

            const signature = await signMessageAsync({
                message,
            })

            // Send the signature to the Feather API
            logger.info('Sending signature to Feather API', {
                quoteId: sellData.featherQuoteId,
                walletAddress: evmWalletAddress,
            })

            const signResponse = await apiSignFeatherQuote(sellData.featherQuoteId, signature, evmWalletAddress)

            if (signResponse.status === 'complete') {
                setSignatureStatus('signed')
            } else {
                logger.error('Failed to sign Feather quote', {
                    quoteId: sellData.featherQuoteId,
                })
                throw new Error('Failed to sign quote')
            }
        } catch (error: any) {
            logger.error('Error signing quote:', error)

            // Check if user rejected the signature
            if (error.code === 4001) {
                toast.error('Signature request was rejected. Please sign the quote to proceed.', {
                    position: 'bottom-right',
                    autoClose: 5000,
                })
            } else {
                toast.error('Failed to sign quote. Please try again.', {
                    position: 'bottom-right',
                    autoClose: 5000,
                })
            }

            setSignatureStatus('error')
        } finally {
            setWaitingForConnectedWalletInteraction(false)
        }
    }

    // Calculate fee amount
    const totalFeeAmount = fees.reduce((total, fee) => total + Number(fee.value || 0), 0)

    // Properly format values with decimal scaling
    // Use appropriate number of decimal places based on the token
    const formattedSellValue = new Balance(sellData.token)
        .setValue(sellData.value)
        .formatWithCommas(sellData.token.symbol?.toUpperCase() === 'BTC' ? 8 : 6)

    // For BTC we want to show 8 decimal places, for other tokens like USDC show 6
    const formattedBuyValue = new Balance(buyData.token)
        .setValue(buyData.value)
        .formatWithCommas(buyData.token.symbol?.toUpperCase() === 'BTC' ? 8 : 6)

    // Calculate USD values
    const sellValueInUSD = sellData.token.price_current
        ? new Balance(sellData.token).setValue(sellData.value).toNumber() * sellData.token.price_current
        : 0

    const buyValueInUSD = buyData.token.price_current
        ? new Balance(buyData.token).setValue(buyData.value).toNumber() * buyData.token.price_current
        : 0

    // Get exchange rate - ensure the direction matches the swap direction
    const isBTCtoUSDC = sellData.token.symbol?.toUpperCase() === 'BTC' && buyData.token.symbol?.includes('USD')
    const exchangeRate = isBTCtoUSDC
        ? `1 BTC ≈ ${new Balance(buyData.token).setValue(buyData.value).toNumber() / new Balance(sellData.token).setValue(sellData.value).toNumber()} ${buyData.token.symbol}`
        : `1 ${sellData.token.symbol} ≈ ${new Balance(buyData.token).setValue(buyData.value).toNumber() / new Balance(sellData.token).setValue(sellData.value).toNumber()} ${buyData.token.symbol}`

    // Determine if steps are in a pending state
    const isPendingStep = (step: StepNames) => {
        return [
            StepNames.FEATHER_WAITING_FOR_DEPOSIT,
            StepNames.FEATHER_DEPOSIT_DETECTED,
            StepNames.FEATHER_PROCESSING,
            StepNames.FEATHER_SENDING_TO_DESTINATION,
        ].includes(step)
    }

    // Handle modal close
    const handleClose = () => {
        // Don't allow closing if we're in a pending state
        if (isPendingStep(StepNames.FEATHER_WAITING_FOR_DEPOSIT)) {
            return
        }

        // Reset waiting state and close modals when closing the component
        setWaitingForConnectedWalletInteraction(false)
        onClose()
    }

    return (
        <div className='flex w-full flex-col space-y-6'>
            <div className='mb-2 flex w-full items-center justify-between px-1'>
                <div className='flex flex-1 items-center'>
                    <ChevronLeftIcon
                        width={24}
                        height={24}
                        onClick={handleClose}
                        className={classNames('cursor-pointer', {
                            'cursor-not-allowed opacity-50': isPendingStep(StepNames.FEATHER_WAITING_FOR_DEPOSIT),
                        })}
                    />
                </div>
                <p className='flex-10 text-2xl font-bold'>Swap Details</p>
                <div className='flex-1'></div>
            </div>

            {/* Warning Notice */}
            <div className='flex items-start gap-3 rounded-lg border border-amber-600/50 bg-amber-900/30 p-4'>
                <span className='mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500'>
                    <WarningIcon />
                </span>
                <p className='text-sm text-amber-200'>
                    Only send {sellData.token.symbol} to this address while the channel is open to avoid a{' '}
                    <span className='underline'>loss of funds</span>
                </p>
            </div>

            {/* Send and Address Section - Always visible */}
            <div className='flex flex-col space-y-4'>
                {/* Send section with large token amount - Without background */}
                <div className='flex flex-col items-center p-5 text-center'>
                    <p className='mb-2 text-gray-300'>Send</p>
                    <div className='mb-2 flex items-center'>
                        <img
                            src={sellData.token.logoURI}
                            alt={sellData.token.symbol}
                            className='mr-2 h-12 w-12 rounded-full'
                        />
                        <span className='text-4xl font-bold text-white'>
                            {formattedSellValue} {sellData.token.symbol}
                        </span>
                    </div>
                    <p className='text-gray-300'>to the deposit address</p>

                    {/* Timer - Integrated into send section as in image 1 */}
                    <div className='mt-3 flex items-center justify-center'>
                        <ClockIcon className='mr-2 h-5 w-5 text-gray-400' />
                        {isExpired ? (
                            <span className='text-amber-400'>EXPIRED</span>
                        ) : (
                            <span className='text-gray-400'>{timerDisplay}</span>
                        )}
                    </div>
                </div>

                {/* Network and Address Section - Always show but content changes based on signature */}
                <div className='relative pt-4'>
                    {/* Network label pill - Always visible */}
                    <div className='absolute left-1/2 top-0 z-10 -translate-x-1/2 -translate-y-1/3 transform'>
                        <div className='flex items-center justify-center rounded-lg bg-gray-700/90 px-6 py-2 shadow-md'>
                            {sourceNetworkIcon && (
                                <img
                                    src={sourceNetworkIcon}
                                    className='mr-2 h-6 w-6 rounded-full'
                                    alt={sourceNetworkName}
                                />
                            )}
                            <span className='text-white'>{sourceNetworkName}</span>
                        </div>
                    </div>

                    {/* Address box - Always visible */}
                    <div className='rounded-lg bg-gray-900 p-4'>
                        {/* QR Toggle row - Always visible but conditionally enabled */}
                        <div
                            className={`mb-4 flex items-center justify-between ${signatureStatus !== 'signed' ? 'opacity-50' : ''}`}
                        >
                            <label
                                className={`flex items-center ${signatureStatus === 'signed' ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                            >
                                <div
                                    className={`mr-2 h-5 w-10 rounded-full ${
                                        signatureStatus === 'signed'
                                            ? showQR
                                                ? 'bg-blue-600'
                                                : 'bg-gray-600'
                                            : 'bg-gray-600'
                                    }`}
                                    onClick={signatureStatus === 'signed' ? toggleQR : undefined}
                                >
                                    <div
                                        className={`h-5 w-5 rounded-full bg-white shadow transition-all ${
                                            showQR && signatureStatus === 'signed' ? 'translate-x-5' : 'translate-x-0'
                                        }`}
                                    ></div>
                                </div>
                                <span
                                    className='ml-1 text-sm text-gray-300'
                                    onClick={signatureStatus === 'signed' ? toggleQR : undefined}
                                >
                                    Show QR
                                </span>
                            </label>
                            <button
                                onClick={signatureStatus === 'signed' ? handleCopyDepositAddress : undefined}
                                className='text-gray-400 hover:text-white'
                                disabled={signatureStatus !== 'signed'}
                                aria-label='Copy address'
                            >
                                <Copy width='1.25rem' height='1.25rem' />
                            </button>
                        </div>

                        {/* Conditionally show deposit address or sign message */}
                        {signatureStatus === 'signed' ? (
                            <>
                                {/* Deposit address - Only shown when signed */}
                                <div className='w-full break-all font-mono text-sm text-gray-300'>{depositAddress}</div>

                                {/* QR Code - Show when toggled and signed */}
                                {showQR && (
                                    <div className='mt-4 flex justify-center rounded-lg bg-white p-4'>
                                        <img
                                            src={`https://chart.googleapis.com/chart?cht=qr&chl=${depositAddress}&chs=200x200&choe=UTF-8&chld=L|2`}
                                            alt='QR Code'
                                            className='h-40 w-40'
                                        />
                                    </div>
                                )}

                                {/* Add deposit options - New section */}
                                <div className='mt-5 flex flex-col gap-3'>
                                    <div className='rounded-lg border border-gray-600 bg-gray-800/30 p-3'>
                                        <p className='mb-2 text-sm font-medium text-white'>Choose how to deposit</p>
                                        <p className='mb-3 text-xs text-gray-300'>
                                            You can either deposit manually by sending {formattedSellValue}{' '}
                                            {sellData.token.symbol} to the address above, or use the button below to
                                            create the transaction automatically.
                                            {EVMChainId !== sellData.chainId && (
                                                <span className='mt-1 block text-amber-400'>
                                                    Your wallet is currently connected to{' '}
                                                    {availableNetworks[EVMChainId || 0]?.name || 'a different network'}.
                                                    Clicking the button below will prompt you to switch to{' '}
                                                    {availableNetworks[sellData.chainId]?.name}.
                                                </span>
                                            )}
                                        </p>

                                        <div className='flex flex-col gap-2'>
                                            <button
                                                onClick={handleSendTransaction}
                                                className='w-full rounded-lg bg-blue-600 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50'
                                                disabled={isTransacting || isExpired}
                                            >
                                                {isTransacting ? (
                                                    <span className='flex items-center justify-center'>
                                                        <svg
                                                            className='-ml-1 mr-2 h-4 w-4 animate-spin text-white'
                                                            xmlns='http://www.w3.org/2000/svg'
                                                            fill='none'
                                                            viewBox='0 0 24 24'
                                                        >
                                                            <circle
                                                                className='opacity-25'
                                                                cx='12'
                                                                cy='12'
                                                                r='10'
                                                                stroke='currentColor'
                                                                strokeWidth='4'
                                                            ></circle>
                                                            <path
                                                                className='opacity-75'
                                                                fill='currentColor'
                                                                d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                                                            ></path>
                                                        </svg>
                                                        {isSwitchingNetwork
                                                            ? 'Switching Network...'
                                                            : 'Creating Transaction...'}
                                                    </span>
                                                ) : isExpired ? (
                                                    'Order Expired'
                                                ) : (
                                                    'Create Transaction Automatically'
                                                )}
                                            </button>

                                            <p className='text-center text-xs text-gray-400'>or</p>

                                            <div className='flex items-center justify-center'>
                                                <span className='text-sm text-gray-300'>
                                                    Deposit manually using your wallet
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : (
                            // Sign prompt only replaces the address display area
                            <div className='flex min-h-[60px] flex-col items-center justify-center rounded-lg bg-gray-800/60 p-4 text-center'>
                                {signatureStatus === 'error' && (
                                    <div className='mb-3 w-full rounded-md bg-red-900/20 p-2 text-sm text-red-300'>
                                        Signing failed. Please try again.
                                    </div>
                                )}

                                {/* Information about signing message */}
                                <p className='mb-2 text-sm text-gray-300'>
                                    You'll be prompted to sign a message to confirm your order. This is only a signature
                                    request and does not incur any fee.
                                </p>

                                <button
                                    onClick={handleSignQuote}
                                    className='mt-3 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50'
                                    disabled={signatureStatus === 'signing' || isExpired}
                                >
                                    {signatureStatus === 'signing'
                                        ? 'Waiting...'
                                        : isExpired
                                          ? 'Expired'
                                          : 'Generate Deposit Address'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Swap Details */}
            <div className='rounded-lg p-4'>
                <h3 className='mb-4 text-lg font-medium text-gray-200'>Swap Details</h3>

                {/* Deposit section */}
                <div className='mb-4'>
                    <p className='mb-2 text-gray-400'>Deposit</p>
                    <div className='flex items-center'>
                        <div className='flex flex-1 items-center'>
                            <img
                                src={sellData.token.logoURI}
                                alt={sellData.token.symbol}
                                className='mr-2 h-8 w-8 rounded-full'
                            />
                            <span className='font-medium text-white'>
                                {formattedSellValue} {sellData.token.symbol}
                            </span>
                        </div>
                        <span className='text-gray-400'>${sellValueInUSD.toFixed(2)}</span>
                    </div>
                </div>

                {/* Receive section */}
                <div className='mb-4'>
                    <p className='mb-2 text-gray-400'>Receive</p>
                    <div className='flex items-center'>
                        <div className='flex flex-1 items-center'>
                            <img
                                src={buyData.token.logoURI}
                                alt={buyData.token.symbol}
                                className='mr-2 h-8 w-8 rounded-full'
                            />
                            <span className='font-medium text-white'>
                                {formattedBuyValue} {buyData.token.symbol}
                            </span>
                        </div>
                        <span className='text-gray-400'>${buyValueInUSD.toFixed(2)}</span>
                    </div>
                </div>

                {/* Fee info */}
                <div className='mb-4'>
                    <div className='flex justify-between'>
                        <p className='text-gray-400'>Est. Total fees</p>
                        <button className='flex items-center text-gray-400'>${totalFeeAmount.toFixed(2)}</button>
                    </div>
                </div>

                {/* Exchange rate */}
                <div className='mb-4'>
                    <div className='flex justify-between'>
                        <p className='text-gray-400'>Est. Rate</p>
                        <p className='text-white'>{exchangeRate}</p>
                    </div>
                </div>
            </div>
        </div>
    )
}
